<template>
  <div
    class="ls"
    :class="`ls--${skinMode}`"
    :style="cssVars"
  >
    <a
      class="ls__skip"
      href="#ls-collection"
    >Skip to the collection</a>
    <ls-masthead
      :logo="logo"
      :brand-name="brandName"
      :sheet-name="mastheadSheetName"
      :shortlist-count="shortlist.length"
      @shortlist="scrollToOrder"
    >
      <template
        v-if="feedbackOn"
        #chip
      >
        <ls-viewer-chip
          :viewer="feedback.state.viewer"
          @not-me="feedback.notMe()"
          @change="feedback.changeIdentity()"
        />
      </template>
    </ls-masthead>
    <main id="ls-main">
      <ls-cover
        :images="presentation.cover || []"
        :headline="presentation.headline || sheetName || brandName"
        :collection-name="collectionName"
        :style-count="styleCount"
      />
      <ls-story
        :intro="presentation.intro || ''"
        :facts="facts"
      />
      <div
        id="ls-collection"
        class="ls__body"
      >
        <ls-section-index
          v-if="indexSections.length"
          class="ls__index"
          :sections="indexSections"
          :active-key="activeKey"
          @browse-all="$emit('browse-all')"
        />
        <div class="ls__sections">
          <ls-section
            v-for="section in sections"
            :key="section.key"
            ref="sections"
            :section="section"
            :product-lines="presentation.product_lines || {}"
            :price-visibility="priceVisibility"
            :shortlist="shortlist"
            :feedback-items="feedbackItems"
            :feedback-on="feedbackOn"
            @toggle="toggle"
            @interest="toggleInterest"
            @open="open"
          />
          <div
            v-if="loadError"
            class="ls__status"
            data-test="ls-load-error"
            role="alert"
          >
            <p>{{ loadError }}</p>
            <button
              type="button"
              @click="retry"
            >Try again</button>
          </div>
          <div
            v-else-if="loading && !sections.length"
            class="ls__skeleton"
            aria-hidden="true"
          >
            <span
              v-for="n in 8"
              :key="n"
            />
          </div>
          <div
            ref="sentinel"
            class="ls__sentinel"
            aria-hidden="true"
          />
          <p
            v-if="loading && sections.length"
            class="ls__more"
            role="status"
          >Loading more styles</p>
        </div>
      </div>
      <ls-sheet-note
        v-if="feedbackOn"
        :brand-name="brandName"
        :note="feedback.state.note.text"
        :status="feedback.state.status"
        :limit="feedback.state.limits.note"
        @input="text => feedback.setNote(text)"
        @commit="feedback.commit()"
      />
      <ls-order-bar
        ref="order"
        :cta="presentation.cta"
        :shortlist-products="shortlistProducts"
        :interested-products="interestedProducts"
        :note="feedbackOn ? feedback.state.note.text : ''"
        :page-url="pageUrl"
        :printing="printing"
        @print="saveAsPdf"
      >
        <template
          v-if="feedbackOn"
          #chip
        >
          <ls-viewer-chip
            :viewer="feedback.state.viewer"
            @not-me="feedback.notMe()"
            @change="feedback.changeIdentity()"
          />
        </template>
      </ls-order-bar>
      <ls-sources
        :brand-name="brandName"
        :website="(presentation.brand && presentation.brand.website) || ''"
        :updated-at="presentation.updated_at || ''"
        :sources="presentation.sources || []"
      />
    </main>
    <ls-footer
      :brand-name="brandName"
      :website="(presentation.brand && presentation.brand.website) || ''"
      :logo="logo"
    />
    <ls-product-detail
      v-if="detailProduct"
      :product="detailProduct"
      :line="(presentation.product_lines || {})[detailProduct._id] || ''"
      :price-visibility="priceVisibility"
      :shortlisted="shortlist.includes(detailProduct._id)"
      :feedback-on="feedbackOn"
      :brand-name="brandName"
      :interested="feedbackOf(detailProduct._id).interested"
      :comment="feedbackOf(detailProduct._id).comment"
      :save-status="feedback.state.status"
      :comment-limit="feedback.state.limits.comment"
      @toggle="toggle"
      @interest="toggleInterest"
      @comment="setComment"
      @commit="feedback.commit()"
      @close="detailId = null"
    />
    <ls-identify-dialog
      v-if="feedback.state.prompt"
      :brand-name="brandName"
      :initial="identityInitial"
      :busy="identifyBusy"
      :error="identifyError"
      @submit="submitIdentity"
      @dismiss="dismissIdentity"
    />
  </div>
</template>

<script>
import {
  fetchPublicProductsPage,
  trackPublicWishlistOpen,
} from '@/composables/useWishlistProducts'
import LsMasthead from './LsMasthead.vue'
import LsCover from './LsCover.vue'
import LsStory from './LsStory.vue'
import LsSectionIndex from './LsSectionIndex.vue'
import LsSection from './LsSection.vue'
import LsOrderBar from './LsOrderBar.vue'
import LsSources from './LsSources.vue'
import LsFooter from './LsFooter.vue'
import LsProductDetail from './LsProductDetail.vue'
import LsSheetNote from './LsSheetNote.vue'
import LsViewerChip from './LsViewerChip.vue'
import LsIdentifyDialog from './LsIdentifyDialog.vue'
import lineSheetFeedback from './lineSheetFeedback.mixin'
import { skinCssVars, fontFaceCss } from './skinStyle'
import { bucketSections, storyFacts } from './lineSheetModel'
import { loadShortlist, saveShortlist, toggleShortlist } from './shortlist'
import { applyDocumentBranding } from './documentBranding'

const FACES_STYLE_ID = 'ls-brand-faces'
const LOAD_ERROR = 'The styles could not load. Check your connection and try again.'
const MAX_PAGES = 50
// While the lookbook is open the body carries this class, so portal-wide
// floating chrome (the scroll-to-top button) stays off the order bar.
const BRANDED_BODY_CLASS = 'ls-branded-open'

/**
 * The branded line sheet (KP-4545): the brand's lookbook built from the
 * presentation plg-go-be prepared (skin, cover, copy, sections) and the live
 * public product payload catalog-service serves (products, prices already
 * stripped to the brand's price visibility).
 *
 * Products load 120 at a time; the first page renders at once and the rest
 * follow as the buyer nears the end, or all together before Save as PDF.
 *
 * KP-4572: likes, interest, comments and a sheet note go to the brand
 * (lineSheetFeedback.mixin); the page never waits on them.
 */
export default {
  name: 'BrandedLineSheet',
  components: {
    LsMasthead,
    LsCover,
    LsStory,
    LsSectionIndex,
    LsSection,
    LsOrderBar,
    LsSources,
    LsFooter,
    LsProductDetail,
    LsSheetNote,
    LsViewerChip,
    LsIdentifyDialog,
  },
  mixins: [lineSheetFeedback],
  props: {
    presentation: { type: Object, required: true },
    publicToken: { type: String, required: true },
    // KP-4572: the email link's tracked link id (ks) and the PLG origin for feedback.
    trackedLinkId: { type: String, default: '' },
    plgBaseUrl: { type: String, default: '' },
  },
  data() {
    return {
      productsById: {},
      loadedOrder: [],
      wishlist: {},
      count: 0,
      nextPage: 0,
      loading: false,
      loadError: '',
      tracked: false,
      shortlist: loadShortlist(this.publicToken),
      detailId: null,
      printing: false,
      activeKey: '',
      restoreDocument: null,
      observer: null,
      sectionObserver: null,
      inflight: null,
      destroyed: false,
    }
  },
  computed: {
    cssVars() {
      return skinCssVars(this.presentation.skin)
    },
    skinMode() {
      return this.presentation.skin && this.presentation.skin.mode === 'dark' ? 'dark' : 'light'
    },
    logo() {
      return this.presentation.skin && this.presentation.skin.logo
    },
    brandName() {
      return (this.presentation.brand && this.presentation.brand.name) || 'Line sheet'
    },
    sheetName() {
      return this.wishlist.name || ''
    },
    // The sheet's name, unless it only repeats the brand's.
    mastheadSheetName() {
      return this.sheetName.trim().toLowerCase() === this.brandName.trim().toLowerCase() ? '' : this.sheetName
    },
    collectionName() {
      return this.wishlist.collectionName || ''
    },
    styleCount() {
      return (this.presentation.facts && this.presentation.facts.styles) || this.count
    },
    priceVisibility() {
      return this.wishlist.priceVisibility
    },
    sections() {
      return bucketSections(this.presentation.sections, this.productsById, { pending: this.hasMore && !this.loadError })
    },
    // The index lists every section, loaded or not, so a buyer can see the
    // whole sheet's shape from the start.
    indexSections() {
      return (this.presentation.sections || []).map(s => ({
        key: s.key,
        title: s.title,
        total: (s.product_ids || []).length,
      }))
    },
    facts() {
      const loaded = this.loadedOrder.map(id => this.productsById[id])
      return storyFacts(this.presentation.facts, this.priceVisibility, loaded)
    },
    shortlistProducts() {
      return this.shortlist.map(id => this.productsById[id]).filter(Boolean)
    },
    detailProduct() {
      return this.detailId ? this.productsById[this.detailId] || null : null
    },
    hasMore() {
      return this.nextPage === 0 || (this.loadedOrder.length < this.count && this.nextPage < MAX_PAGES)
    },
    pageUrl() {
      if (typeof window === 'undefined') return ''
      return `${window.location.origin}${window.location.pathname}`
    },
  },
  mounted() {
    document.body.classList.add(BRANDED_BODY_CLASS)
    this.injectFaces()
    this.loadAll()
    this.watchSentinel()
  },
  beforeDestroy() {
    this.destroyed = true
    document.body.classList.remove(BRANDED_BODY_CLASS)
    const faces = document.getElementById(FACES_STYLE_ID)
    if (faces) faces.remove()
    if (this.restoreDocument) this.restoreDocument()
    if (this.observer) this.observer.disconnect()
    if (this.sectionObserver) this.sectionObserver.disconnect()
  },
  methods: {
    injectFaces() {
      const css = fontFaceCss(this.presentation.skin)
      if (!css) return
      const style = document.createElement('style')
      style.id = FACES_STYLE_ID
      style.textContent = css
      document.head.appendChild(style)
    },
    brandDocument() {
      if (this.restoreDocument) return
      const title = [this.mastheadSheetName, this.brandName].filter(Boolean).join(' | ')
      this.restoreDocument = applyDocumentBranding({ title, faviconUrl: this.logo && this.logo.url })
    },
    // One page at a time: a caller arriving while a page loads waits for
    // that page instead of starting another.
    loadNextPage() {
      if (this.inflight) return this.inflight
      if (!this.hasMore) return Promise.resolve()
      this.inflight = this.fetchNextPage().finally(() => { this.inflight = null })
      return this.inflight
    },
    async fetchNextPage() {
      this.loading = true
      this.loadError = ''
      try {
        const page = await fetchPublicProductsPage(this.$store.dispatch, this.publicToken, this.nextPage)
        if (!this.destroyed) this.acceptPage(page)
      } catch (e) {
        this.loadError = LOAD_ERROR
      } finally {
        this.loading = false
      }
    },
    acceptPage(page) {
      const fresh = page.products.filter(p => p && p._id && !this.productsById[p._id])
      this.productsById = { ...this.productsById, ...Object.fromEntries(fresh.map(p => [p._id, p])) }
      this.loadedOrder = [...this.loadedOrder, ...fresh.map(p => p._id)]
      this.count = page.count
      if (this.nextPage === 0) {
        this.wishlist = page.wishlist
        this.brandDocument()
        this.$nextTick(this.watchSections)
      }
      this.nextPage += 1
      // An empty page means the server has nothing more, whatever count said.
      if (!page.products.length) this.count = this.loadedOrder.length
      this.pruneShortlist()
      if (!this.tracked) {
        this.tracked = true
        trackPublicWishlistOpen({ ...page.wishlist, count: page.count }, 'branded')
      }
    },
    // Once every style is in, a stored id the sheet no longer holds is dropped,
    // so the masthead count matches the order bar. Not before: at the page cap
    // an id may belong to a page never fetched.
    pruneShortlist() {
      if (this.loadedOrder.length < this.count) return
      const kept = this.shortlist.filter(id => this.productsById[id])
      if (kept.length === this.shortlist.length) return
      this.shortlist = kept
      saveShortlist(this.publicToken, kept)
    },
    stopped() {
      return this.destroyed || Boolean(this.loadError)
    },
    // Every page, one at a time: the first renders at once, the rest fill the
    // places their sections already hold.
    async loadAll() {
      while (this.hasMore && !this.stopped()) {
        // eslint-disable-next-line no-await-in-loop
        await this.loadNextPage()
      }
    },
    // Try again resumes the whole load, not just the page that failed.
    retry() {
      this.loadError = ''
      return this.loadAll()
    },
    async saveAsPdf() {
      if (this.printing) return
      this.printing = true
      try {
        await this.loadAll()
        // A failed page would print a sheet silently missing styles; the
        // error with Try again stays on screen instead.
        if (this.loadError || this.hasMore) return
        await this.$nextTick()
        window.print()
      } finally {
        this.printing = false
      }
    },
    watchSentinel() {
      if (typeof window.IntersectionObserver !== 'function' || !this.$refs.sentinel) return
      this.observer = new window.IntersectionObserver(entries => {
        if (entries.some(e => e.isIntersecting)) this.loadNextPage()
      }, { rootMargin: '1200px 0px' })
      this.observer.observe(this.$refs.sentinel)
    },
    watchSections() {
      if (typeof window.IntersectionObserver !== 'function' || this.sectionObserver) return
      this.sectionObserver = new window.IntersectionObserver(entries => {
        const visible = entries.filter(e => e.isIntersecting)
        if (visible.length) this.activeKey = visible[0].target.id.replace(/^ls-sec-/, '')
      }, { rootMargin: '-120px 0px -60% 0px' })
      this.observeSections()
    },
    observeSections() {
      if (!this.sectionObserver) return
      this.$el.querySelectorAll('.ls-section').forEach(el => this.sectionObserver.observe(el))
    },
    toggle(id) {
      this.shortlist = toggleShortlist(this.shortlist, id)
      saveShortlist(this.publicToken, this.shortlist)
      this.feedback.setItem(id, { liked: this.shortlist.includes(id) })
    },
    open(id) {
      this.detailId = id
    },
    scrollToOrder() {
      const el = this.$refs.order && this.$refs.order.$el
      if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'center' })
    },
  },
  watch: {
    'sections.length': function observeNewSections() {
      this.$nextTick(this.observeSections)
    },
  },
}
</script>

<style lang="scss" scoped>
@import './ls-base';

.ls {
  min-height: 100vh;
  background: var(--ls-ground);
  color: var(--ls-ink);
  font-family: var(--ls-body-font);
  font-weight: var(--ls-body-weight);
  letter-spacing: var(--ls-body-tracking);
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  overflow-x: clip;

  &__skip {
    position: absolute;
    left: 16px;
    top: -64px;
    z-index: 100;
    padding: 12px 16px;
    background: var(--ls-ink);
    color: var(--ls-ground);
    font-weight: 600;

    &:focus {
      top: 8px;
    }
  }

  &__body {
    max-width: $ls-max;
    margin: 0 auto;

    @include ls-up($ls-xl) {
      display: grid;
      grid-template-columns: 220px minmax(0, 1fr);
      gap: 56px;
      padding: 0 $ls-gutter;
    }
  }

  &__index {
    @include ls-up($ls-xl) {
      align-self: start;
      margin-top: clamp(40px, 7vw, 88px);
    }
  }

  &__sections {
    padding: 0 $ls-gutter;
    min-width: 0;

    @include ls-up($ls-xl) {
      padding: 0;
    }
  }

  &__status {
    margin-top: 48px;
    padding: 24px;
    border: 1px solid var(--ls-line);
    border-radius: var(--ls-radius);

    p {
      margin: 0 0 16px;
    }

    button {
      min-height: 44px;
      padding: 0 20px;
      border: 1px solid var(--ls-ink);
      border-radius: var(--ls-radius);
      background: transparent;
      color: var(--ls-ink);
      font-weight: 600;
      cursor: pointer;
      @include ls-focus;
    }
  }

  &__skeleton {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 32px 12px;
    margin-top: 56px;

    @include ls-up($ls-md) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    @include ls-up($ls-xl) {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }

    span {
      aspect-ratio: 4 / 5;
      background: var(--ls-surface);
      border-radius: var(--ls-radius);
    }
  }

  &__sentinel {
    height: 1px;
  }

  &__more {
    margin: 32px 0 0;
    color: var(--ls-muted);
    text-align: center;
  }
}
</style>

<style lang="scss">
@import './ls-print';
</style>
